import { useState, useEffect, useCallback, useRef } from 'react';
import { Outlet, useLocation } from '@tanstack/react-router';
import {
  Separator,
  SidebarInset,
  SidebarProvider,
  SidebarTrigger,
  cn,
} from '@loader/ui';
import { useUser } from '@clerk/clerk-react';

import { BreadcrumbNav } from '@/components/layout/BreadcrumbNav';
import { usePostHogIdentify } from '@/lib/posthog';
import { SidebarCollapseManager } from '@/components/layout/SidebarCollapseManager';
import { Toaster } from '@/components/ui';
import { SearchCommandDialog } from '@/components/common';
import { UpgradeDialog } from '@/components/billing';
import { useSubscription } from '@/hooks/useSubscription';
import { useWorkspaceStore } from '@/store/useWorkspaceStore';
import { WorkspaceSidebar } from '@/features/organization/components/WorkspaceSidebar';
import { SidePanel } from '@/features/workspace/components/SidePanel';
import { ExpandedItemContent } from '@/features/workspace/components/ExpandedItemContent';
import { useSidePanelStore } from '@/features/workspace/stores';
import { useTheme } from '@/hooks/useTheme';
import { useWorkspaceMembership } from '@/hooks/useWorkspaceMembership';
import { OnboardingDialog, useOnboardingStore } from '@/features/onboarding';

function useCrispIdentify() {
  const { user, isSignedIn } = useUser();
  const identified = useRef(false);

  useEffect(() => {
    if (!isSignedIn) {
      identified.current = false;
      return;
    }
    if (!user || identified.current) return;

    const email = user.primaryEmailAddress?.emailAddress;
    const name = user.fullName;
    if (!email) return;

    window.$crisp?.push(['set', 'user:email', [email]]);
    if (name) {
      window.$crisp?.push(['set', 'user:nickname', [name]]);
    }
    identified.current = true;
  }, [isSignedIn, user]);
}

/**
 * Root Shell
 *
 * Minimal shell rendered by __root.tsx for ALL routes.
 * Only handles theme and renders the router outlet.
 * Auth gating and workspace resolution are handled by layout routes.
 */
export function AppShell() {
  useTheme();

  return (
    <>
      <Outlet />
      <Toaster />
    </>
  );
}

/**
 * App Shell Content
 *
 * Full workspace chrome with sidebar, breadcrumb, side panel, and dialogs.
 * Rendered by the _withWorkspace layout route ONLY after workspace is resolved.
 * By the time this component mounts, selectedWorkspaceId is guaranteed to be set.
 */
export function AppShellContent() {
  const location = useLocation();
  const { selectedWorkspaceId } = useWorkspaceStore();

  // Identify user with PostHog (production only)
  usePostHogIdentify();

  // Identify user with Crisp chat
  useCrispIdentify();

  // Onboarding carousel (auto-opens for first-time users, scoped to user ID)
  const { user } = useUser();
  const openIfFirstTime = useOnboardingStore((s) => s.openIfFirstTime);
  useEffect(() => {
    if (user?.id) {
      openIfFirstTime(user.id);
    }
  }, [openIfFirstTime, user?.id]);

  const { isExpired, isLoading: isSubscriptionLoading } = useSubscription(selectedWorkspaceId || '');
  const { isOpen: isSidePanelOpen, mode: sidePanelMode, isExpanded: isSidePanelExpanded, previewItem, close: closeSidePanel } = useSidePanelStore();
  const { canEdit } = useWorkspaceMembership();

  // Track if panel should be rendered (for exit animation)
  const [shouldRenderPanel, setShouldRenderPanel] = useState(false);
  const [isAnimating, setIsAnimating] = useState(false);

  // Track expanded overlay animation
  const [shouldRenderExpanded, setShouldRenderExpanded] = useState(false);
  const [isExpandedAnimating, setIsExpandedAnimating] = useState(false);

  // Search command dialog state
  const [isSearchOpen, setIsSearchOpen] = useState(false);

  // Keyboard shortcut for search (Cmd+K / Ctrl+K)
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        setIsSearchOpen((open) => !open);
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, []);

  // Handler for sidebar search button
  const handleSearchClick = useCallback(() => {
    setIsSearchOpen(true);
  }, []);

  // Check if current route supports side panels (workspace routes)
  const isWorkspaceRoute = location.pathname.startsWith('/workspace');

  // Pages that should be accessible even with expired subscription
  const isWorkspaceSettingsPage = location.pathname === '/settings/workspace' || location.pathname.startsWith('/settings/workspace');
  const isBillingPage = location.pathname === '/settings/billing' || location.pathname.startsWith('/settings/billing');

  // Check if subscription is expired (but not while loading subscription data)
  const showExpiredDialog = !isSubscriptionLoading && isExpired && !isWorkspaceSettingsPage && !isBillingPage && !!selectedWorkspaceId;

  // Show side panel only on workspace routes
  const showSidePanel = isWorkspaceRoute && isSidePanelOpen && sidePanelMode !== null;

  // Panel animation - uses 300ms to match overlay animation
  useEffect(() => {
    if (showSidePanel) {
      setShouldRenderPanel(true);
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          setIsAnimating(true);
        });
      });
    } else if (shouldRenderPanel) {
      setIsAnimating(false);
      const timer = setTimeout(() => {
        setShouldRenderPanel(false);
      }, 300);
      return () => clearTimeout(timer);
    }
    // shouldRenderPanel intentionally excluded - we only want to react to showSidePanel changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showSidePanel]);

  // Expanded overlay animation
  useEffect(() => {
    if (isSidePanelExpanded) {
      setShouldRenderExpanded(true);
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          setIsExpandedAnimating(true);
        });
      });
    } else if (shouldRenderExpanded) {
      setIsExpandedAnimating(false);
      const timer = setTimeout(() => {
        setShouldRenderExpanded(false);
      }, 300);
      return () => clearTimeout(timer);
    }
    // shouldRenderExpanded intentionally excluded - we only want to react to isSidePanelExpanded changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSidePanelExpanded]);

  return (
    <SidebarProvider>
      <SidebarCollapseManager />
      <WorkspaceSidebar onSearch={handleSearchClick} />
      <SidebarInset className="overflow-hidden">
        <div className="relative flex h-full">
          {/* Main content */}
          <div className="relative flex h-full flex-1 flex-col">
            <header className="flex h-16 shrink-0 items-center gap-2 transition-[width,height] ease-linear group-has-data-[collapsible=icon]/sidebar-wrapper:h-12">
              <div className="flex items-center gap-2 px-4">
                <SidebarTrigger className="-ml-1" aria-label="Toggle sidebar" />
                <Separator
                  orientation="vertical"
                  className="mr-2 data-[orientation=vertical]:h-4"
                />
                <BreadcrumbNav />
              </div>
            </header>
            <main className="relative flex flex-1 flex-col overflow-hidden bg-auto px-4">
              <Outlet />
            </main>
          </div>

          {/* Side panel - animates width */}
          {shouldRenderPanel && (
            <div
              className={cn(
                "h-full shrink-0 overflow-hidden border-l bg-background transition-[width,opacity] duration-300 ease-out",
                isAnimating ? "w-[400px] opacity-100" : "w-0 opacity-0"
              )}
            >
              <div className="h-full w-[400px]">
                <SidePanel onClose={closeSidePanel} />
              </div>
            </div>
          )}

          {/* Expanded content overlay - covers entire content area including side panel */}
          {shouldRenderExpanded && (
            <div
              className={cn(
                "absolute inset-0 z-20 overflow-hidden bg-background transition-all duration-300 ease-out",
                isExpandedAnimating ? "opacity-100" : "translate-x-full opacity-0"
              )}
            >
              {previewItem ? (
                <ExpandedItemContent item={previewItem} canEdit={canEdit} />
              ) : (
                <div className="flex h-full items-center justify-center text-muted-foreground">
                  No item selected
                </div>
              )}
            </div>
          )}
        </div>
      </SidebarInset>

      {/* Subscription Expired Paywall - non-dismissable */}
      <UpgradeDialog
        context="subscription_expired"
        open={showExpiredDialog}
        onOpenChange={() => {
          // Don't allow closing the dialog - user must upgrade
        }}
        workspaceId={selectedWorkspaceId || ''}
      />

      {/* Search Command Dialog */}
      <SearchCommandDialog open={isSearchOpen} onOpenChange={setIsSearchOpen} />

      {/* Onboarding Carousel */}
      <OnboardingDialog />
    </SidebarProvider>
  );
}
